<div class="px-4 py-6 md:px-10 md:py-10">
  <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 mb-6">
    <div class="flex flex-col gap-3 xl:flex-row xl:items-center">
      <h1 class="text-xl font-bold text-primary-dark md:text-2xl xl:whitespace-nowrap">Ciclos de Visitação</h1>

      <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div class="date-range-wrapper w-full sm:w-[200px]">
          <app-date-range-picker
            [initialRange]="{ startDate: selectedPeriodStart(), endDate: selectedPeriodEnd() }"
            placeholder="Selecione o período"
            (dateRangeChanged)="onDateRangeChange($event)"
          ></app-date-range-picker>
        </div>

        <ng-select
          class="filter-select w-full sm:w-[180px]"
          [items]="consultantOptions()"
          bindLabel="label"
          bindValue="value"
          placeholder="Todos os Consultores"
          [clearable]="true"
          [searchable]="true"
          (change)="onConsultantChange($event)"
        ></ng-select>

        <ng-select
          class="filter-select w-full sm:w-[180px]"
          [items]="statusOptions"
          bindLabel="label"
          bindValue="value"
          placeholder="Todos os Status"
          [clearable]="true"
          [multiple]="true"
          (change)="onStatusChange($event)"
        ></ng-select>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row sm:items-center xl:ml-auto">
        <button
          type="button"
          class="header-btn-outline w-full sm:w-auto"
          [disabled]="isExportingPdf()"
          (click)="exportToPdf()"
        >
          {{ isExportingPdf() ? 'Exportando PDF...' : 'Exportar em PDF' }}
        </button>
        <button
          type="button"
          class="header-btn-primary w-full sm:w-auto"
          (click)="navigateToNew()"
        >
          + Novo Ciclo
        </button>
      </div>
    </div>
  </div>

  @if (cards().length > 0) {
    <div class="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
      <p class="m-0 text-sm text-gray-600">{{ resultSummary() }}</p>

      <div class="flex items-center gap-2">
        <label for="ciclo-page-size" class="text-sm text-gray-600 whitespace-nowrap">
          Itens exibidos
        </label>
        <div class="relative">
          <select
            id="ciclo-page-size"
            class="page-size-select"
            [ngModel]="pageSize()"
            (ngModelChange)="onPageSizeChange($event)"
          >
            @for (option of pageSizeOptions; track option) {
              <option [ngValue]="option">{{ option }}</option>
            }
          </select>
          <mat-icon class="page-size-select-icon">expand_more</mat-icon>
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <div class="flex justify-center items-center py-20">
      <div class="w-10 h-10 rounded-full border-4 border-gray-200 border-t-primary animate-spin"></div>
    </div>
  } @else if (cards().length === 0) {
    <div class="flex flex-col items-center justify-center py-20 text-gray-500">
      <mat-icon class="text-6xl mb-4">event_busy</mat-icon>
      <p class="text-lg">Nenhum ciclo de visitação encontrado</p>
      <p class="text-sm">Tente ajustar os filtros ou crie um novo ciclo</p>
    </div>
  } @else {
    <div class="grid grid-cols-1 gap-4 w-full lg:grid-cols-2">
      @for (card of cards(); track card.id) {
        <div
          class="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition cursor-pointer flex flex-col h-full"
          (click)="onCardClick(card)"
        >
          <div class="flex items-start justify-between gap-2 mb-3">
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-primary-dark truncate" [title]="card.name">
                {{ card.name }}
              </h3>
            </div>
            <div class="flex items-center justify-end gap-2 flex-shrink-0 flex-wrap">
              <app-badge [variant]="getStatusVariant(card.status)">
                {{ card.statusLabel }}
              </app-badge>
              @if (card.activityTag !== ConsultantActivityTag.None) {
                <app-badge [variant]="getActivityTagVariant(card.activityTag)">
                  {{ card.activityTagLabel }}
                </app-badge>
              }
            </div>
          </div>

          <div class="flex items-center gap-3 mb-4">
            @if (card.consultant.avatarUrl) {
              <img
                [src]="card.consultant.avatarUrl"
                [alt]="card.consultant.name"
                class="w-8 h-8 rounded-full object-cover"
                onerror="this.src='assets/img/default-consultant-avatar.png'"
              />
            } @else {
              <div class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                {{ getInitials(card.consultant.name) }}
              </div>
            }
            <span class="text-sm text-gray-700">{{ card.consultant.name }}</span>
          </div>

          <!-- Cycle Info -->
          <div class="space-y-2 text-sm text-gray-600 mb-4">
            <div class="flex items-center gap-2">
              <mat-icon class="text-gray-400 leading-none" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">autorenew</mat-icon>
              <span>{{ card.periodLabel }}</span>
            </div>
            <div class="flex items-center gap-2">
              <mat-icon class="text-gray-400 leading-none" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">route</mat-icon>
              <span>{{ card.routeDays }} dias de rota</span>
              @if (card.internalDays > 0) {
                <span class="text-gray-400">•</span>
                <mat-icon class="text-gray-400 leading-none" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">apartment</mat-icon>
                <span>{{ card.internalDays }} dias internos</span>
              }
            </div>
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1">
                <mat-icon class="text-gray-400 leading-none" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">store</mat-icon>
                <span>{{ card.totalStores }} lojas</span>
              </div>
              <div class="flex items-center gap-1">
                <mat-icon class="text-gray-400 leading-none" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px;">add_road</mat-icon>
                <span>{{ card.totalKm }} km</span>
              </div>
            </div>
          </div>

          <!-- Spacer: empurra banner+progresso+footer pra base quando card tem altura maior que o conteúdo -->
          <div class="flex-1"></div>

          @if (card.banner?.kind === CycleCardBannerKind.CurrentRoute) {
            <div class="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
              <div class="flex items-center gap-2">
                <span class="text-blue-600 font-medium text-sm">Rota Atual</span>
              </div>
              <p class="text-sm mt-1">{{ card.banner!.dayNumber }} | {{ card.banner!.dayTitle }}</p>
            </div>
          } @else if (card.banner?.kind === CycleCardBannerKind.LastExecutedPaused) {
            <div class="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
              <div class="flex items-center gap-2">
                <span class="text-red-600 font-medium text-sm">Ciclo Pausado - Última Rota Executada</span>
              </div>
              <p class="text-sm mt-1">
                {{ formatExecutedDate(card.banner!.executedDate) }} – {{ card.banner!.dayNumber }} | {{ card.banner!.dayTitle }}
              </p>
            </div>
          }

          <div class="mb-4">
            <div class="flex items-center justify-between text-sm mb-1">
              <span class="text-gray-600 font-medium">Progresso</span>
              <span class="text-gray-500">
                {{ card.progress.completedDays }} de {{ card.progress.totalDays }} dias ({{ card.progress.percentComplete }}%)
              </span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-300"
                [class]="card.progress.percentComplete === 100 ? 'bg-green-500' : 'bg-primary'"
                [style.width]="getProgressBarWidth(card.progress)"
              ></div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2 flex-wrap pt-3 border-t border-gray-100">
            <button
              type="button"
              class="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
              (click)="navigateToCalendar(card.id); $event.stopPropagation()"
            >
              Ver calendário
            </button>
            <div class="flex items-center gap-2">
              @if (canEditCycle(card)) {
                <button
                  type="button"
                  class="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                  (click)="navigateToEdit(card.id); $event.stopPropagation()"
                >
                  Editar ciclo
                </button>
              }
              <button
                type="button"
                class="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
                [matMenuTriggerFor]="cardMenu"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #cardMenu="matMenu">
                @if (canEditCycle(card)) {
                  <button mat-menu-item (click)="navigateToEdit(card.id)">
                    <mat-icon>edit</mat-icon>
                    <span>Editar ciclo</span>
                  </button>
                }
                <button mat-menu-item (click)="navigateToCalendar(card.id)">
                  <mat-icon>calendar_today</mat-icon>
                  <span>Ver calendário</span>
                </button>
                @if (canPause(card)) {
                  <button mat-menu-item (click)="confirmPause(card, $event)">
                    <mat-icon>pause_circle</mat-icon>
                    <span>Pausar ciclo</span>
                  </button>
                }
                @if (canResume(card)) {
                  <button mat-menu-item (click)="confirmResume(card, $event)">
                    <mat-icon>play_circle</mat-icon>
                    <span>Retomar ciclo</span>
                  </button>
                }
                @if (canCancel(card)) {
                  <button mat-menu-item class="text-red-600" (click)="confirmCancel(card, $event)">
                    <mat-icon class="text-red-600">cancel</mat-icon>
                    <span>Cancelar ciclo</span>
                  </button>
                }
                @if (canDelete(card)) {
                  <button mat-menu-item class="text-red-600" (click)="confirmDelete(card, $event)">
                    <mat-icon class="text-red-600">delete</mat-icon>
                    <span>Excluir ciclo</span>
                  </button>
                }
              </mat-menu>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <div class="h-24"></div>
</div>
